import React, { useEffect, useState } from 'react';

// PWA install prompt for Firmanager Mobile (ESS).
// Shows a friendly bottom sheet:
//   • On Android/desktop Chrome+Edge → uses the native `beforeinstallprompt` event.
//   • On iOS Safari (which has no install prompt API) → shows an instructions card
//     explaining the Share → "Add to Home Screen" flow.
// Dismissals are stored in localStorage so we don't nag the user.

const DISMISS_KEY = 'fm_ess_install_dismissed_until';
// Re-prompt 14 days after a "later" dismiss, never again after "installed".
const SNOOZE_DAYS = 14;

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

const dismissedRecently = () => {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
};

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosTip, setShowIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    const onInstalled = () => {
      setShow(false);
      setShowIosTip(false);
      try {
        // Remember forever once installed.
        localStorage.setItem(DISMISS_KEY, String(Date.now() + 365 * 24 * 60 * 60 * 1000));
      } catch {
        // ignore quota
      }
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari: no event fires, so show our manual tip after a short delay.
    if (isIOS()) {
      const t = setTimeout(() => setShowIosTip(true), 1500);
      return () => {
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
        window.removeEventListener('appinstalled', onInstalled);
        clearTimeout(t);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const snooze = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000));
    } catch {
      // ignore
    }
    setShow(false);
    setShowIosTip(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'dismissed') snooze();
    } catch {
      // ignore
    }
    setDeferredPrompt(null);
    setShow(false);
  };

  if (!show && !showIosTip) return null;

  const sheetStyle = {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    background: '#0f172a',
    color: '#f8fafc',
    borderTop: '1px solid #1e293b',
    boxShadow: '0 -8px 24px rgba(0,0,0,0.5)',
    padding: '16px 18px calc(16px + env(safe-area-inset-bottom))',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };
  const titleStyle = { fontWeight: 600, fontSize: 15, margin: 0 };
  const textStyle = { fontSize: 13, lineHeight: 1.4, color: '#cbd5e1', margin: 0 };
  const rowStyle = { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 };
  const btnPrimary = {
    background: '#2563eb', color: 'white', border: 'none',
    padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  };
  const btnSecondary = {
    background: 'transparent', color: '#94a3b8', border: '1px solid #334155',
    padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
  };

  if (show && deferredPrompt) {
    return (
      <div style={sheetStyle} role="dialog" aria-label="Install Firmanager Mobile">
        <p style={titleStyle}>📲 Install Firmanager Mobile</p>
        <p style={textStyle}>
          Add the app to your home screen for faster access, offline support and a full-screen experience.
        </p>
        <div style={rowStyle}>
          <button type="button" style={btnSecondary} onClick={snooze}>Not now</button>
          <button type="button" style={btnPrimary} onClick={handleInstall}>Install</button>
        </div>
      </div>
    );
  }

  // iOS instructions
  return (
    <div style={sheetStyle} role="dialog" aria-label="Install Firmanager Mobile on iOS">
      <p style={titleStyle}>📲 Install Firmanager Mobile</p>
      <p style={textStyle}>
        Tap the <strong>Share</strong> button in Safari and choose <strong>“Add to Home Screen”</strong>{' '}
        to install Firmanager as an app on your iPhone.
      </p>
      <div style={rowStyle}>
        <button type="button" style={btnSecondary} onClick={snooze}>Got it</button>
      </div>
    </div>
  );
}
